<template>
  <div class="relative inline-block text-left" ref="root">
    <!-- Trigger -->
    <button
        type="button"
        class="inline-flex items-center gap-2 rounded-lg
         px-4 py-2.5 text-base  <!-- was px-3 py-2 text-sm -->
         bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
         ring-1 ring-gray-300/70 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750
         shadow-sm transition"
        @click="toggleOpen"
        :aria-expanded="open.toString()"
    >
      <CalendarIcon class="w-5 h-5" /> <!-- was w-4 h-4 -->
      <span>{{ formatted }}</span>
      <ChevronDownIcon class="w-5 h-5 opacity-70" /> <!-- was w-4 h-4 -->
    </button>


    <!-- Popover -->
    <div
        v-if="open"
        class="absolute z-50 w-72 origin-top rounded-xl
             bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
             ring-1 ring-gray-300/70 dark:ring-gray-700 shadow-xl p-3"
        :class="popoverPosClasses"
    >
      <div class="grid grid-cols-[auto,1fr,auto] items-center mb-2 text-gray-700 dark:text-gray-200">
      <button
          class="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          @click="prevMonth"
          aria-label="Previous month"
      >
        <!-- Left chevron inline -->
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
          <path fill-rule="evenodd"
                d="M12.79 5.23a.75.75 0 0 1 0 1.06L9.06 10l3.73 3.71a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
                clip-rule="evenodd" />
        </svg>
      </button>

      <div class="text-sm font-semibold text-center">
        {{ monthLabel }} {{ viewYear }}
      </div>

      <button
          class="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          @click="nextMonth"
          aria-label="Next month"
      >
        <!-- Right chevron inline -->
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
          <path fill-rule="evenodd"
                d="M7.21 14.77a.75.75 0 0 1 0-1.06L10.94 10 7.2 6.29a.75.75 0 1 1 1.06-1.06l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0Z"
                clip-rule="evenodd" />
        </svg>
      </button>
    </div>


      <!-- Weekdays -->
      <div class="grid grid-cols-7 text-xs text-gray-500 dark:text-gray-400 mb-1 select-none">
        <div v-for="d in weekdays" :key="d" class="text-center py-1">{{ d }}</div>
      </div>

      <!-- Days -->
      <div class="grid grid-cols-7 gap-1">
        <div v-for="n in leadingBlanks" :key="'b'+n" class="h-9" />
        <button
            v-for="d in daysInMonth"
            :key="d"
            class="h-9 rounded-lg text-sm flex items-center justify-center
                 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            :class="dayBtnClass(d)"
            @click="selectDay(d)"
        >
          {{ d }}
        </button>
      </div>

      <!-- Actions -->
      <div class="mt-3 flex items-center justify-between">
        <button
            class="text-xs text-gray-600 dark:text-gray-300 underline hover:opacity-80"
            @click="goToday"
        >
          Today
        </button>
        <div class="flex gap-2">
          <button
              class="text-sm px-3 py-1 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-650"
              @click="open=false"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Props:
 *  - modelValue: 'YYYY-MM-DD'
 * Emits:
 *  - update:modelValue
 */
export default {
  name: 'UiDatePicker',
  props: {
    modelValue: { type: String, default: () => new Date().toISOString().slice(0,10) },
  },
  emits: ['update:modelValue'],
  data() {
    const today = this.toDate(this.modelValue) || new Date();
    return {
      open: false,
      viewMonth: today.getMonth(),
      viewYear: today.getFullYear(),
      placeRight: false,
      placeAbove: false,
    };
  },
  computed: {
    formatted() {
      const d = this.toDate(this.modelValue);
      return d ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Select date';
    },
    monthLabel() {
      return new Date(this.viewYear, this.viewMonth, 1).toLocaleString('en-US', { month: 'long' });
    },
    weekdays() { return ['Su','Mo','Tu','We','Th','Fr','Sa']; },
    daysInMonth() {
      return new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
    },
    firstWeekday() {
      return new Date(this.viewYear, this.viewMonth, 1).getDay();
    },
    leadingBlanks() {
      return this.firstWeekday;
    },
    selectedParts() {
      const d = this.toDate(this.modelValue);
      return d ? { y: d.getFullYear(), m: d.getMonth(), d: d.getDate() } : null;
    },
    popoverPosClasses() {
      const h = this.placeRight ? 'right-0' : 'left-0';
      const v = this.placeAbove ? 'bottom-full mb-2' : 'top-full mt-2';
      return `${h} ${v}`;
    },
  },
  methods: {
    toDate(iso) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '')) return null;
      const [y,m,d] = iso.split('-').map(Number);
      const dt = new Date(Date.UTC(y, m-1, d));
      return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
    },
    toISO(y, m, d) {
      const mm = String(m+1).padStart(2,'0');
      const dd = String(d).padStart(2,'0');
      return `${y}-${mm}-${dd}`;
    },
    dayBtnClass(d) {
      const isSelected = this.selectedParts && this.selectedParts.y === this.viewYear && this.selectedParts.m === this.viewMonth && this.selectedParts.d === d;
      const today = new Date();
      const isToday = (this.viewYear === today.getFullYear() && this.viewMonth === today.getMonth() && d === today.getDate());
      return {
        'bg-indigo-600 text-white hover:bg-indigo-600': isSelected,
        'ring-1 ring-indigo-500': !isSelected && isToday,
      };
    },
    selectDay(d) {
      const iso = this.toISO(this.viewYear, this.viewMonth, d);
      this.$emit('update:modelValue', iso);
      this.open = false;
    },
    prevMonth() {
      if (this.viewMonth === 0) { this.viewMonth = 11; this.viewYear--; } else { this.viewMonth--; }
    },
    nextMonth() {
      if (this.viewMonth === 11) { this.viewMonth = 0; this.viewYear++; } else { this.viewMonth++; }
    },
    goToday() {
      const t = new Date();
      this.viewYear = t.getFullYear();
      this.viewMonth = t.getMonth();
      this.$emit('update:modelValue', this.toISO(t.getFullYear(), t.getMonth(), t.getDate()));
      this.open = false;
    },
    handleClickOutside(e) {
      if (this.open && this.$refs.root && !this.$refs.root.contains(e.target)) this.open = false;
    },
    toggleOpen() {
      this.open = !this.open;
      if (this.open) {
        this.$nextTick(() => this.recalcPlacement());
      }
    },
    recalcPlacement() {
      const POPOVER_W = 288;
      const POPOVER_H = 320;
      const root = this.$refs.root;
      if (!root) return;

      const rect = root.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      this.placeRight = rect.left + POPOVER_W > vw && rect.right - POPOVER_W >= 0;
      this.placeAbove = rect.bottom + POPOVER_H > vh && rect.top - POPOVER_H >= 0;
    }
  },
  watch: {
    modelValue(val) {
      const d = this.toDate(val);
      if (d) { this.viewYear = d.getFullYear(); this.viewMonth = d.getMonth(); }
    }
  },
  mounted() {
    window.addEventListener('click', this.handleClickOutside);
    window.addEventListener('resize', this.recalcPlacement);
    window.addEventListener('scroll', this.recalcPlacement, true);
  },
  beforeUnmount() {
    window.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('resize', this.recalcPlacement);
    window.removeEventListener('scroll', this.recalcPlacement, true);
  },
  components: {
    CalendarIcon: {
      template: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z"/></svg>'
    },
    ChevronLeftIcon: {
      template: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M12.79 5.23a.75.75 0 0 1 0 1.06L9.06 10l3.73 3.71a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clip-rule="evenodd"/></svg>`
    },
    ChevronRightIcon: {
      template: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M7.21 14.77a.75.75 0 0 1 0-1.06L10.94 10 7.2 6.29a.75.75 0 1 1 1.06-1.06l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0Z" clip-rule="evenodd"/></svg>`
    },
    ChevronDownIcon: {
      template: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 0 1 1.08 1.04l-4.25 4.25a.75.75 0 0 1-1.06 0L5.21 8.27a.75.75 0 0 1 .02-1.06Z" clip-rule="evenodd"/></svg>`
    },
  }
};
</script>

<style scoped>
.dark .hover\:bg-gray-750:hover { background-color: #2a2f3a; }
</style>
